import React, { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import { cloneDeep } from "lodash";
import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";

import { CUSTOM_LINK_TYPES } from "lookup";
import { validateOtherLink } from "utils/validation";
import { MatchContext, SearchContext } from "context/providers";

import AppDialog from "components/AppDialog";
import Input from "components/base/Input";
import FormLabel, {
  FORM_INPUT_CLASSNAME,
  FORM_LABEL_CLASSNAME,
} from "components/base/FormLabel";
import { Button } from "components/ui/button";

const LINK_SUCCESS_MESSAGE = "Link successfully added";

const TYPE_OPTIONS = [
  { label: "External", value: CUSTOM_LINK_TYPES.EXTERNALLINK },
  { label: "General", value: CUSTOM_LINK_TYPES.GENERAL },
  { label: "Result", value: CUSTOM_LINK_TYPES.RESULT },
  { label: "Resume", value: CUSTOM_LINK_TYPES.RESUME },
  { label: "Other", value: CUSTOM_LINK_TYPES.OTHER },
];

const VISIBILITY_OPTIONS = [
  { label: "Customer", value: "CUSTOMER" },
  { label: "Public", value: "PUBLIC" },
  { label: "Private", value: "PRIVATE" },
];

const LINK_DEFAULT_INPUTS = {
  name: {
    value: "",
    errorMessage: "",
  },
  value: {
    value: "",
    errorMessage: "",
  },
  description: {
    value: "",
    errorMessage: "",
  },
  type: {
    value: CUSTOM_LINK_TYPES.EXTERNALLINK,
    errorMessage: "",
  },
  visibility: {
    value: "CUSTOMER",
    errorMessage: "",
  },
};

const selectComponents = {
  IndicatorSeparator: () => null,
  DropdownIndicator: (props) => (
    <components.DropdownIndicator {...props}>
      <ChevronDown className="h-4 w-4 text-slate-500" />
    </components.DropdownIndicator>
  ),
};

const selectStyles = {
  valueContainer: (base) => ({
    ...base,
    padding: "8px 12px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 50,
  }),
};

const CustomLinkModal = ({ user, modalTarget, applicationId, onClose }) => {
  const { updateHit } = useContext(SearchContext);
  const { updateMatchFieldLocally } = useContext(MatchContext);

  const [customLinkData, setCustomLinkData] = useState({
    successMessage: "",
    errorMessage: "",
    isSubmitting: false,
    inputs: cloneDeep(LINK_DEFAULT_INPUTS),
  });

  const clearOtherLinkData = () => {
    onClose();
    setCustomLinkData((prev) => ({
      ...prev,
      successMessage: "",
      errorMessage: "",
      isSubmitting: false,
      inputs: cloneDeep(LINK_DEFAULT_INPUTS),
    }));
  };

  const handleTextChange = useCallback((valueKey, value) => {
    setCustomLinkData((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [valueKey]: { ...prev.inputs[valueKey], value, errorMessage: "" },
      },
    }));
  }, []);

  const handleSelectChange = useCallback((valueKey, option) => {
    setCustomLinkData((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [valueKey]: {
          ...prev.inputs[valueKey],
          value: option?.value ?? "",
          errorMessage: "",
        },
      },
    }));
  }, []);

  const prepareOtherLinksInputs = useCallback(() => {
    const output = {};

    Object.keys(customLinkData.inputs).forEach(
      (inputKey) => (output[inputKey] = customLinkData.inputs[inputKey].value)
    );

    return output;
  }, [customLinkData.inputs]);

  const updateOtherLinksByKey = (key, value) => {
    setCustomLinkData((prev) => ({ ...prev, [key]: value }));
  };

  const addOtherLink = useCallback(
    async (linkData) => {
      try {
        const { id } = user;
        const otherLinks = user.otherLinks || [];
        const newOtherLinks = [...otherLinks, linkData];

        updateOtherLinksByKey("isSubmitting", true);
        updateOtherLinksByKey("errorMessage", "");

        await updateHit({
          input: {
            id,
            otherLinks: newOtherLinks,
          },
        });

        updateOtherLinksByKey("successMessage", LINK_SUCCESS_MESSAGE);

        if (modalTarget === "Match") {
          updateMatchFieldLocally(
            applicationId,
            "user.otherLinks",
            newOtherLinks
          );
        }
      } catch (error) {
        console.warn("addOtherLink error", error);

        updateOtherLinksByKey(
          "errorMessage",
          error?.errors?.[0]?.message || "Something went wrong"
        );
      }

      updateOtherLinksByKey("isSubmitting", false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, modalTarget, applicationId]
  );

  const setOtherLinksErrors = useCallback((errors) => {
    setCustomLinkData((prev) => {
      const inputs = {
        ...prev.inputs,
      };

      Object.keys(errors).forEach(
        (errorKey) => (inputs[errorKey].errorMessage = errors[errorKey])
      );

      return { ...prev, inputs };
    });
  }, []);

  const submitOtherLinksModal = useCallback(() => {
    const data = prepareOtherLinksInputs();

    validateOtherLink({
      data,
      onSuccess: (validData) => addOtherLink(validData),
      onError: (errors) => setOtherLinksErrors(errors),
    });
  }, [prepareOtherLinksInputs, setOtherLinksErrors, addOtherLink]);

  const { inputs, isSubmitting, successMessage, errorMessage } = customLinkData;

  const typeOption =
    TYPE_OPTIONS.find((option) => option.value === inputs.type.value) ||
    TYPE_OPTIONS[0];
  const visibilityOption =
    VISIBILITY_OPTIONS.find(
      (option) => option.value === inputs.visibility.value
    ) || VISIBILITY_OPTIONS[0];

  return (
    <AppDialog
      open={!!user}
      onClose={clearOtherLinkData}
      header="Add Link"
      className="w-full max-w-2xl overflow-visible rounded-lg"
      contentClassName="overflow-visible px-6 pb-6 pt-4"
    >
      <div className="grid grid-cols-12 gap-x-4 gap-y-1">
        <div className="col-span-12 md:col-span-6">
          <Input
            className="mb-3"
            label="Name"
            labelClassName={FORM_LABEL_CLASSNAME}
            inputClassName={FORM_INPUT_CLASSNAME}
            value={inputs.name.value}
            placeholder="Name of link"
            isRequired
            errorMessage={inputs.name.errorMessage}
            onChange={({ value }) => handleTextChange("name", value)}
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <Input
            className="mb-3"
            label="Value"
            labelClassName={FORM_LABEL_CLASSNAME}
            inputClassName={FORM_INPUT_CLASSNAME}
            value={inputs.value.value}
            placeholder="Valid URL"
            isRequired
            errorMessage={inputs.value.errorMessage}
            onChange={({ value }) => handleTextChange("value", value)}
          />
        </div>

        <div className="col-span-12">
          <Input
            className="mb-3"
            label="Description"
            labelClassName={FORM_LABEL_CLASSNAME}
            inputClassName={FORM_INPUT_CLASSNAME}
            value={inputs.description.value}
            placeholder="Describe link"
            errorMessage={inputs.description.errorMessage}
            onChange={({ value }) => handleTextChange("description", value)}
          />
        </div>

        <div className="col-span-12 mb-3 flex flex-col gap-1 md:col-span-6">
          <FormLabel htmlFor="link-type">Link type</FormLabel>
          <Select
            inputId="link-type"
            value={typeOption}
            options={TYPE_OPTIONS}
            onChange={(option) => handleSelectChange("type", option)}
            isSearchable={false}
            menuShouldScrollIntoView={false}
            components={selectComponents}
            styles={selectStyles}
            className="w-full"
          />
          {inputs.type.errorMessage ? (
            <p className="font-inter text-sm font-bold text-red-500">
              {inputs.type.errorMessage}
            </p>
          ) : null}
        </div>

        <div className="col-span-12 mb-3 flex flex-col gap-1 md:col-span-6">
          <FormLabel htmlFor="link-visibility">Link visibility</FormLabel>
          <Select
            inputId="link-visibility"
            value={visibilityOption}
            options={VISIBILITY_OPTIONS}
            onChange={(option) => handleSelectChange("visibility", option)}
            isSearchable={false}
            menuShouldScrollIntoView={false}
            components={selectComponents}
            styles={selectStyles}
            className="w-full"
          />
          {inputs.visibility.errorMessage ? (
            <p className="font-inter text-sm font-bold text-red-500">
              {inputs.visibility.errorMessage}
            </p>
          ) : null}
        </div>

        {successMessage ? (
          <p className="col-span-12 font-inter text-sm font-medium text-green-600">
            {successMessage}
          </p>
        ) : null}
        {errorMessage ? (
          <p className="col-span-12 font-inter text-sm font-medium text-red-500">
            {errorMessage}
          </p>
        ) : null}

        <div className="col-span-12 mt-2 flex justify-end gap-2.5">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={clearOtherLinkData}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-sky-500 hover:bg-sky-600"
            disabled={isSubmitting}
            onClick={submitOtherLinksModal}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
};

CustomLinkModal.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  modalTarget: PropTypes.string.isRequired,
  applicationId: PropTypes.string,
  collectionKey: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

CustomLinkModal.defaultProps = {
  user: null,
  applicationId: "",
  collectionKey: "hits",
};

export default CustomLinkModal;
